import loadScript from 'simple-load-script';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { offline as errorToast, updateToSuccess } from '../../toast';

const MathJaxContext = createContext(null);

const toastId = 'mathjax-failed';

const MathJaxErrorToast = () => {
  errorToast(
    `
        Failed to load math renderer.
        This could lead to math expressions not displaying correctly.
        Please try refreshing the page.
      `,
    {
      autoclose: 60 * 1000,
      toastId,
    },
  );
};


const INITIAL_TIMEOUT_MS = 20 * 1000;
const TIMEOUT_STEP_MS = 10 * 1000;
const MAX_RETRIES = 6;

const Provider = (props) => {
  const url = props.url || DEFAULT_URL;
  const options = props.options || DEFAULT_OPTIONS;

  const [retry, setRetry] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMathJaxLoaded, setIsMathJaxLoaded] = useState(false);
  const loadGenerationRef = useRef(0);
  const contextMenuHandlerRef = useRef(null);

  useEffect(() => {
    //solve race condition where retry triggers during initialization?
    if (isInitialized) return;
    window.MathJax = options;
    if (window.localStorage) {
      //remove stale menu configuration
      window.localStorage.removeItem('MathJax-Menu-Settings');
    }
    // Increment generation before scheduling anything so every closure below
    // captures the same value and can detect whether it has been superseded.
    const generation = ++loadGenerationRef.current;
    let timeoutId;
    if (retry <= MAX_RETRIES) {
      timeoutId = setTimeout(() => {
        if (generation !== loadGenerationRef.current) return;
        onErr('MathJax load timed out');
        setRetry((retry) => retry + 1);
      }, retry === 0 ? INITIAL_TIMEOUT_MS : retry * TIMEOUT_STEP_MS); // 20s first time, then 10s, 20s, 30s...
    }
    window.MathJax.startup = {
      ...options?.startup,
      started: false,
      ready: () => {
        if (generation !== loadGenerationRef.current) return;
        if (window.MathJax?.startup?.started) return;
        window.MathJax.startup.started = true;
        window.MathJax.startup.defaultReady();
        setIsInitialized(true);
        // Suppress the MathJax right-click context menu. The menu lets users change
        // renderer settings that can break rendering, and those settings persist in
        // localStorage. We block the event rather than using enableMenu:false because
        // that option crashes ui/safe in MathJax v4.
        const onContextMenu = (e) => {
          if (e.target.closest('mjx-container, .MathJax')) {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        contextMenuHandlerRef.current = onContextMenu;
        document.addEventListener('contextmenu', onContextMenu, true);
        window.MathJax.startup.promise.then(() => {
          clearTimeout(timeoutId);
          updateToSuccess(toastId, { autoClose: 3 * 1000, render: 'Math renderer loaded.' });
        });
      }
    };
    // Remove stale script tags from prior attempts before loading
    if (retry > 0) {
      document.querySelectorAll(`script[src="${url}"]`).forEach(s => s.remove());
    }
    loadScript(url).then(() => onLoad(generation)).catch((error) => {
      if (generation !== loadGenerationRef.current) return;
      onErr(error);
    });
    return () => {
      clearTimeout(timeoutId);
      if (contextMenuHandlerRef.current) {
        document.removeEventListener('contextmenu', contextMenuHandlerRef.current, true);
        contextMenuHandlerRef.current = null;
      }
    };
  }, [url, options, retry, isInitialized]);

  function onLoad(generation) {
    // Discard callbacks from stale load attempts (e.g. a previous retry's script resolving late)
    if (generation !== loadGenerationRef.current) return;
    //short-circuit if already initialized
    if (window.MathJax?.startup?.started) return;
    setIsMathJaxLoaded(true);
    //V2 Portability - https://docs.mathjax.org/en/v3.0/upgrading/v2.html#version-2-compatibility-example
    window.MathJax.Callback = function (args) {
      if (Array.isArray(args)) {
        if (args.length === 1 && typeof args[0] === 'function') {
          return args[0];
        } if (
          typeof args[0] === 'string'
          && args[1] instanceof Object
          && typeof args[1][args[0]] === 'function'
        ) {
          return Function.bind.apply(args[1][args[0]], args.slice(1));
        } if (typeof args[0] === 'function') {
          return Function.bind.apply(args[0], [window].concat(args.slice(1)));
        } if (typeof args[1] === 'function') {
          return Function.bind.apply(args[1], [args[0]].concat(args.slice(2)));
        }
      } else if (typeof args === 'function') {
        return args;
      }
      throw Error("Can't make callback from given data");
    };

    window.MathJax.Hub = {
      Queue() {
        for (let i = 0, m = arguments.length; i < m; i++) {
          // eslint-disable-next-line prefer-rest-params
          const fn = window.MathJax.Callback(arguments[i]);
          window.MathJax.startup.promise = window.MathJax.startup.promise.then(fn);
        }
        return window.MathJax.startup.promise;
      },
      Typeset(elements, callback) {
        let promise = window.MathJax.typesetPromise(elements);
        if (callback) {
          promise = promise.then(callback);
        }
        return promise;
      },
      getAllJax: () => window.MathJax.startup.document.getMathItemsWithin(document.body),
      Register: {
        MessageHook() {
          console.log('MessageHooks are not supported in version 3');
        },
        StartupHook() {
          console.log('StartupHooks are not supported in version 3');
        },
        LoadHook() {
          console.log('LoadHooks are not supported in version 3');
        },
      },
      Config() {
        console.log('MathJax configurations should be converted for version 3');
      },
    };
  }

  function onErr(error) {
    MathJaxErrorToast();
    setIsError(true);
    Sentry.withScope((scope) => {
      scope.setContext('Error Information', {
        message: error,
      });
      scope.setTag('exception', 'Script failed to load.');
      Sentry.captureMessage(error);
    });
  }

  return <MathJaxContext.Provider value={{
    MathJax: window.MathJax,
    isError,
    isInitialized,
    isMathJaxLoaded,
    MathJaxErrorToast,
  }} {...props} />;
};

const DEFAULT_OPTIONS = {};
const DEFAULT_URL = 'https://cdn.jsdelivr.net/npm/mathjax@3.0.1/es5/tex-mml-chtml.js';

export const useMathJaxContext = () => useContext(MathJaxContext);

export default React.memo(Provider);
